import { t } from "@lingui/core/macro";

import { Table, ColumnDef } from "_/components/table";
import { TextLink, Link, NeverUnderlineSpace } from "_/components/router";
import { NumberBadge } from "_/components/number-badge";
import { StatusBadge } from "_/components/badge";
import { Button } from "_/components/button";
import { DateTime } from "_/components/date-time";

import { projectUrls, machineUrls } from "_/routes";

import { JobRow } from "./useJobsData";

type PrintingTableProps = {
  rows: JobRow[];
  compact?: boolean;
  loading?: boolean;
};

export const PrintingTable = ({
  rows,
  compact = false,
  loading,
}: PrintingTableProps) => {
  const columns: (ColumnDef<JobRow> | false)[] = [
    {
      id: "revision",
      header: t`components.queue.column-revision`,
      cell: ({ row }) => {
        const { projectId, projectName, revisionId, revisionNumber } =
          row.original;
        if (!projectId) {
          return revisionId.slice(0, 8);
        }
        return (
          <TextLink
            variant="default"
            to={projectUrls.prepare(projectId, revisionId)}
          >
            <span>{projectName}</span>
            <NeverUnderlineSpace />
            {revisionNumber != null ? (
              <NumberBadge number={revisionNumber} />
            ) : (
              "-"
            )}
          </TextLink>
        );
      },
    },
    {
      id: "machine",
      header: t`common.machine`,
      cell: ({ row }) => {
        const { machine, attempt } = row.original;
        if (!machine) return attempt.machineId.slice(0, 8);
        return (
          <Link to={machineUrls.index(attempt.machineId)}>{machine.name}</Link>
        );
      },
    },
    {
      id: "status",
      header: t`common.status`,
      cell: ({ row }) => <StatusBadge status={row.original.attempt.status} />,
    },
    !compact && {
      id: "started",
      header: t`common.started`,
      cell: ({ row }) => {
        const { startedAt } = row.original.attempt;
        return <DateTime value={startedAt} />;
      },
    },
    {
      id: "submittedBy",
      header: t`components.queue.column-submitter`,
      cell: ({ row }) => {
        const { submittedBy } = row.original;
        if (!submittedBy) return "--";
        return <span title={submittedBy.email}>{submittedBy.name}</span>;
      },
    },
    !compact && {
      id: "queuedAt",
      header: t`components.queue.column-submitted`,
      cell: ({ row }) => {
        const { submittedAt } = row.original;
        if (!submittedAt) return "--";
        return <DateTime value={submittedAt} />;
      },
    },
    {
      id: "actions",
      header: t`common.actions`,
      cell: ({ row }) => {
        const { projectId, attempt } = row.original;
        if (!projectId) return null;
        return (
          <Link to={projectUrls.analyze(projectId, attempt.id)}>
            <Button size="small">{t`common.analyze`}</Button>
          </Link>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns.filter(Boolean)}
      data={rows}
      loading={loading}
      noDataLabel={t`components.jobs.printing-empty`}
    />
  );
};
